import { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useForm, Controller } from 'react-hook-form';
import {
  useGetOrganizationsQuery,
  useGetEmailDomainsQuery,
  useAddEmailDomainMutation,
  useUpdateEmailDomainMutation,
  useRemoveEmailDomainMutation,
} from '../../api/organizationApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

export default function EmailDomainsPage() {
  const { data: organizations, isLoading: loadingOrgs } = useGetOrganizationsQuery();
  const [organizationId, setOrganizationId] = useState('');
  const [editingDomain, setEditingDomain] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const showToast = useToast();

  useAutoSelectFirst(organizations, organizationId, setOrganizationId);

  const {
    data: emailDomains,
    isLoading: loadingDomains,
    isError: isDomainsError,
    error: domainsError,
  } = useGetEmailDomainsQuery(organizationId, { skip: !organizationId });

  const [addEmailDomain, { isLoading: isAdding }] = useAddEmailDomainMutation();
  const [updateEmailDomain, { isLoading: isUpdating }] = useUpdateEmailDomainMutation();
  const [removeEmailDomain] = useRemoveEmailDomainMutation();

  const { control, handleSubmit, reset } = useForm({ defaultValues: { domain: '' } });

  useEffect(() => {
    if (isDomainsError) {
      showToast('error', 'Email domains', errMsg(domainsError, 'Could not load email domains.'));
    }
  }, [isDomainsError, domainsError, showToast]);

  const openAddDialog = () => {
    setEditingDomain(null);
    reset({ domain: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (domain) => {
    setEditingDomain(domain);
    reset({ domain: domain.domain });
    setDialogOpen(true);
  };

  const domainErrMsg = (err) => {
    if (err?.status === 409) return 'That domain is already added to this organization.';
    if (err?.status === 404) return 'This domain no longer exists.';
    return errMsg(err, 'Could not save domain.');
  };

  const onSubmitDomain = async (data) => {
    const domain = data.domain.trim().replace(/^@/, '').toLowerCase();
    if (!domain) return;
    try {
      if (editingDomain) {
        await updateEmailDomain({ id: editingDomain.id, organizationId, domain }).unwrap();
        showToast('success', 'Email domains', 'Domain updated.');
      } else {
        await addEmailDomain({ organizationId, domain }).unwrap();
        showToast('success', 'Email domains', 'Domain added.');
      }
      setDialogOpen(false);
    } catch (err) {
      showToast('error', 'Email domains', domainErrMsg(err));
    }
  };

  async function handleRemove(domain) {
    try {
      await removeEmailDomain({ id: domain.id, organizationId }).unwrap();
      showToast('success', 'Email domains', 'Domain removed.');
    } catch (err) {
      showToast('error', 'Email domains', domainErrMsg(err));
    }
  }

  const actionBodyTemplate = (domain) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => openEditDialog(domain)} />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleRemove(domain)}
      />
    </div>
  );

  if (loadingOrgs) return <p className="textMuted">Loading…</p>;
  if (!organizations?.length)
    return (
      <div className={s.page}>
        <h1>Email domains</h1>
        <p className={`${s.subtitle} textMuted`}>
          Create an organization first, then add email domains to it.
        </p>
      </div>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Email domains</h1>
          <p className={`${s.subtitle} textMuted`}>
            Subscribers must use an email from one of these domains to join the organization.
          </p>
        </div>
        <Button
          label="Add domain"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={openAddDialog}
          disabled={!organizationId}
        />
      </div>

      <div className={`field ${s.orgSelect}`}>
        <label htmlFor="organization">Organization</label>
        <Dropdown
          inputId="organization"
          value={organizationId}
          onChange={(e) => setOrganizationId(e.value)}
          options={organizations.map((o) => ({ label: `${o.name} (${o.slug})`, value: o.id }))}
          className="w-full"
        />
      </div>

      <div className={table.tableWrapper}>
        <DataTable
          value={emailDomains ?? []}
          loading={loadingDomains}
          emptyMessage="No email domains in this organization yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="domain"
            header="Domain"
            className={table.contactCol}
            body={(d) => <div className={table.cellInsider}>@{d.domain}</div>}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '172px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={editingDomain ? 'Edit email domain' : 'Add email domain'}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        style={{ width: '420px' }}
        modal
      >
        <form onSubmit={handleSubmit(onSubmitDomain)}>
          <Controller
            name="domain"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="field">
                <label htmlFor={field.name}>Domain</label>
                <InputText
                  id={field.name}
                  className="w-full mb-3"
                  placeholder="example.edu"
                  autoFocus
                  {...field}
                />
              </div>
            )}
          />
          <Button
            type="submit"
            label={editingDomain ? 'Save' : 'Add'}
            className={buttons.saveBtn}
            loading={isAdding || isUpdating}
          />
        </form>
      </Dialog>
    </div>
  );
}
